import React, { useContext, useEffect, useState } from "react";
import AuthContext from "../context/AuthContext";
import { Home, Search, LogOut, Package, Map, Plus, Send, Calendar1, Images } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axios";
import { useModal } from "./ModalManager";
import { useAppSocket, useProvideAppSocket } from "../hooks/useAppSocket";
import { useBadgeCounters } from "../hooks/useBadgeCounters";
import { fetchUserProfile } from "../api/UserAPI";

function Sidebar({ toggleNotifications, buttonRef }) {
  const { isAuthenticated, user, logout, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState("");
  const { openModal } = useModal();
  // badges
  const { ws, status } = useProvideAppSocket();
  const { counters } = useBadgeCounters({ ws, status });

  // logging only
  // useEffect(() => {
  //   console.log("ws: ", ws);
  //   console.log("status", status);
  //   console.log("BADGES: ", counters);
  // }, [ws, status, counters]);

  // init load
  useEffect(() => {
    if (!token || !user) return;

    const ac = new AbortController();
    let canceled = false;
    (async () => {
      try {
        const profile = await fetchUserProfile(user.username, token);

        if (canceled || ac.signal.aborted) return;

        setProfile(profile);
      } catch (err) {
        if (!canceled) {
          console.error(err);
        }
      }
    })();

    return () => {
      canceled = true;
      ac.abort();
    };
  }, [token, user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const bubbleLink =
    "flex items-center justify-center xl:justify-start gap-2 rounded-full border border-gray-200 bg-white p-2.5 sm:p-3 xl:px-4 xl:py-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-500 hover:shadow-md";

  return (
    <>
      <aside
        className={[
          "fixed z-[9999] bg-white transition-all duration-200",

          // mobile: bottom nav
          "left-0 right-0 bottom-0 h-20 border-t border-gray-200 flex flex-row items-center justify-around px-4",

          // sm+: left sidebar rail
          "sm:top-0 sm:bottom-0 sm:left-0 sm:right-auto sm:h-auto sm:w-20 sm:border-t-0 sm:flex-col sm:justify-between sm:px-0",

          // xl+: full sidebar
          "xl:w-56",
        ].join(" ")}
      >
        {/* TOP */}
        <div className="hidden sm:flex p-4 mb-auto">
          <img
            src={`${process.env.PUBLIC_URL}/blorp_logo.png`}
            className="w-16 h-16 object-contain flex-shrink-0"
            alt="Blorp logo"
          />
          <div className="hidden xl:flex flex-col">
            <h1 className="text-2xl font-bold">blorp</h1>
            <p className="ml-4 text-sm">
              <i>alpha</i>
            </p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <nav className="flex flex-row items-center justify-between gap-3 w-full sm:flex-col sm:justify-start sm:gap-0 sm:space-y-4 sm:p-4">
            <Link to="/" state={{ refreshFeed: true }} className={bubbleLink}>
              <Home className="h-5 w-5" />
              <span className="hidden xl:inline font-medium">Home</span>
            </Link>

            <Link to="/search" className={bubbleLink}>
              <Search className="h-5 w-5" />
              <span className="hidden xl:inline font-medium">Search</span>
            </Link>

            <Link to="/messages/" className={bubbleLink}>
              <div className="relative flex items-center gap-2">
                {counters.messages > 0 && (
                  <span className="w-5 h-5 flex items-center justify-center border border-black bg-purple-600 text-white text-xs rounded-full absolute -top-1 -left-3">
                    {counters.messages}
                  </span>
                )}
                <Send className="h-5 w-5" />
                <span className="hidden xl:inline font-medium">Messages</span>
              </div>
            </Link>

            <button ref={buttonRef} onClick={toggleNotifications} className={bubbleLink}>
              <div className="relative flex items-center gap-2">
                {counters.notifications > 0 && (
                  <span className="w-5 h-5 flex items-center justify-center border border-black bg-purple-600 text-white text-xs rounded-full absolute -top-1 -left-3">
                    {counters.notifications}
                  </span>
                )}
                <Package className="h-5 w-5" />
                <span className="hidden xl:inline font-medium">Notifications</span>
              </div>
            </button>

            <Link to="/media" className={bubbleLink}>
              <Images className="h-5 w-5" />
              <span className="hidden xl:inline font-medium">Media</span>
            </Link>

            <Link to="/calendar" className={bubbleLink}>
              <Calendar1 className="h-5 w-5" />
              <span className="hidden xl:inline font-medium">Calendar</span>
            </Link>

            <Link to="/create" className={bubbleLink}>
              <Plus className="h-5 w-5" />
              <span className="hidden xl:inline font-medium">Create</span>
            </Link>

            {isAuthenticated && user && (
              <Link to={`profiles/${user.username}`} className={bubbleLink}>
                <img
                  src={profile.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                  alt="Profile"
                  className="w-6 h-6 rounded-full object-cover border border-gray-30 shrink-0"
                />
                <span className="hidden xl:inline font-medium">Profile</span>
              </Link>
            )}
          </nav>
        </div>
        <div className="hidden sm:block mt-auto p-4">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 hover:text-blue-500"
          >
            <LogOut />
            <span className="hidden xl:inline font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
